import { api } from "./api";
import { z } from "zod";
import type { SubscriptionFeatures, Plan } from "./api";

const CreditTransactionSchema = z.object({
  id: z.number(),
  amount: z.number(),
  balance: z.number(),
  source: z.string(),
  description: z.string(),
  createdAt: z.string(),
});

const CreditHistorySchema = z.object({
  balance: z.number(),
  history: z.array(CreditTransactionSchema),
  pagination: z.object({
    limit: z.number(),
    offset: z.number(),
  }),
});

const CreditPackSchema = z.object({
  id: z.string(),
  name: z.string(),
  credits: z.number(),
  price: z.number(),
});

const CreditPacksResponseSchema = z.object({
  creditPacks: z.array(CreditPackSchema),
});

const PortalResponseSchema = z.object({
  url: z.string().url(),
});

const MessageResponseSchema = z.object({
  message: z.string(),
});

// type CreditTransaction = z.infer<typeof CreditTransactionSchema>;
type CreditHistory = z.infer<typeof CreditHistorySchema>;
type CreditPack = z.infer<typeof CreditPackSchema>;

export const SubscriptionSchema = z.object({
  id: z.number(),
  userId: z.number(),
  stripeCustomerId: z.string(),
  stripeSubscriptionId: z.string(),
  plan: z.string(),
  billingCycle: z.enum(["month", "year", "monthly"]),
  status: z.enum([
    "active",
    "canceled",
    "past_due",
    "unpaid",
    "incomplete",
    "incomplete_expired",
    "paused",
  ]),
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  cancelAtPeriodEnd: z.boolean(),
  monthlyCredits: z.number(),
  currentCredits: z.number(),
  nextRenewalDate: z.string(),
  metadata: z.any().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const SubscriptionResponseSchema = z.object({
  subscription: SubscriptionSchema,
  credits: z.number(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;
export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>;

const subscriptionApi = {
  /**
   * Get current subscription status
   * @protected Requires authentication
   */
  getCurrentSubscription: async (): Promise<SubscriptionResponse> => {
    const response = await api.get("subscriptions/me").json();
    return SubscriptionResponseSchema.parse(response);
  },

  /**
   * Get subscription features for the current user
   * This is specifically used during onboarding to determine available features
   * @protected Requires authentication
   */
  getSubscriptionFeatures: async (): Promise<SubscriptionFeatures> => {
    const response = await api.get("subscriptions/features").json();
    return response as SubscriptionFeatures;
  },

  /**
   * Get available subscription plans
   */
  getPlans: async (): Promise<Plan[]> => {
    const response = await api.get("subscriptions/plans").json();
    return response as Plan[];
  },

  /**
   * Get credits for the current user
   * @protected Requires authentication
   */
  getCredits: async (options?: {
    limit?: number;
    offset?: number;
  }): Promise<CreditHistory> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());

    const response = await api
      .get(
        `subscriptions/credits${params.toString() ? `?${params.toString()}` : ""}`,
      )
      .json();
    return CreditHistorySchema.parse(response);
  },

  /**
   * Get available credit packs
   * @protected Requires authentication
   */
  getCreditPacks: async (): Promise<{ creditPacks: CreditPack[] }> => {
    const response = await api.get("subscriptions/credit-packs").json();
    return CreditPacksResponseSchema.parse(response);
  },

  /**
   * Create a checkout session for a subscription plan
   * @param planId - The ID of the plan to subscribe to
   * @param interval - Billing interval ('month' or 'year')
   * @protected Requires authentication
   */
  createCheckoutSession: async (
    planId: string,
    interval: "month" | "year",
  ): Promise<{ url: string }> => {
    const response = await api
      .post("subscriptions/checkout", { json: { planId, interval } })
      .json();
    return PortalResponseSchema.parse(response);
  },

  /**
   * Cancel the current subscription plan
   * @protected Requires authentication
   */
  cancelPlan: async (
    cancelImmediately: boolean,
  ): Promise<{ message: string }> => {
    const response = await api
      .post("subscriptions/cancel", { json: { cancelImmediately } })
      .json();
    return MessageResponseSchema.parse(response);
  },

  /**
   * Create a Stripe customer portal session
   * @protected Requires authentication
   */
  createPortalSession: async (returnUrl: string): Promise<{ url: string }> => {
    const response = await api
      .post("subscriptions/portal", { json: { returnUrl } })
      .json();
    return PortalResponseSchema.parse(response);
  },
};

export default subscriptionApi;
